// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Hidden from "@insite/mobius/Hidden";
import { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { xIsAuthenticated } from "@pacific/App/Store/Selectors/GeneralSelectors";
import { getCustomTypeSession } from "@pacific/App/Store/Selectors/SessionSelectors";
import Prokeep from "@pacific/XDL/Icons/Prokeep";
import { CustomSessoinModel } from "@pacific/XDL/types";
import { color, xScreen } from "@pacific/XDL/Utilities";
import { getDataLayer } from "@pacific/XDL/Utilities/dataLayer";
import { xRegex } from "@pacific/XDL/Utilities/validation";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    phoneNumber = "phoneNumber",
    email = "email",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.phoneNumber]: string;
        [fields.email]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    isAuthenticated: xIsAuthenticated(state),
    session: getCustomTypeSession(state),
    // XNG-Change
    currentBillTo: getCurrentBillToState(state).value,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface SupportInformationStyles {
    wrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    phoneWrapper?: InjectableCss;
    phone?: LinkPresentationProps;
    email?: LinkPresentationProps;
    hours?: TypographyPresentationProps;
    phoneHoursWrapper?: InjectableCss;
    prokeep?: LinkPresentationProps;
}

const supportInformationStyles: SupportInformationStyles = {
    titleWrapper: {
        css: css`
            margin-bottom: 11px;
            padding-bottom: 7px;
            border-bottom: 1px solid ${color("common.ui2", 0.2)};
            max-width: 200px;
            ${xScreen("lte", "md")} {
                max-width: none;
            }
        `,
    },
    title: {
        variant: "h6",
        transform: "capitalize",
        css: css`
            margin-bottom: 0;
        `,
    },
    phoneWrapper: {
        css: css`
            &&& svg {
                width: 16px;
                height: 16px;
            }
        `,
    },
    phone: {
        color: "common.ui1",
        typographyProps: {
            variant: "p",
            weight: 600,
            css: css`
                padding-right: 8px;
                margin-bottom: 0;
            `,
        },
    },
    email: {
        color: "common.ui1",
        typographyProps: {
            variant: "p",
            weight: 600,
            css: css`
                padding-right: 8px;
                margin-bottom: 0;
                margin-top: 16px;
            `,
        },
    },
    hours: {
        size: "12px",
        weight: 500,
        lineHeight: "18px",
        css: css`
            letter-spacing: 0;
            opacity: 0.5;
        `,
    },
    phoneHoursWrapper: {
        css: css`
            display: flex;
            ${xScreen("lte", "md")} {
                & > div {
                    flex-basis: 50%;
                }
            }
        `,
    },
    prokeep: {
        css: css`
            margin-top: 6px;
            ${IconWrapper}, svg {
                width: 112px;
                height: 33px;
            }
        `,
    },
};

const styles = supportInformationStyles;

export const SupportInformation: React.FC<Props> = ({ fields, isAuthenticated, session, currentBillTo }) => {
    const defaultWarehouse = session?.defaultWarehouse;
    const warehouseHours = defaultWarehouse?.hours;
    const phoneNumber = !isAuthenticated ? fields.phoneNumber : defaultWarehouse?.phone || fields.phoneNumber;
    const email = !isAuthenticated ? fields.email : defaultWarehouse?.email || fields.email;

    const _GaFooterTelEvent = () => {
        const dataLayer = getDataLayer();
        if (!dataLayer) {
            return;
        }
        const googleAnalyticsData = {
            event: "footer_phonecall",
            click_text: phoneNumber,
            destination: `tel:${phoneNumber}`,
            authentication_state: isAuthenticated ? "Authenticated" : "Guest",
            affiliation: currentBillTo?.companyName || "",
            sales_rep: (session as any)?.salesperson?.name || "",
        };
        dataLayer.push(googleAnalyticsData);
    };

    const _GaFooterEmailEvent = () => {
        const dataLayer = getDataLayer();
        if (!dataLayer) {
            return;
        }
        const googleAnalyticsData = {
            event: "footer_email",
            click_text: email,
            destination: `mailto:${email}`,
            authentication_state: isAuthenticated ? "Authenticated" : "Guest",
            affiliation: currentBillTo?.companyName || "",
            sales_rep: (session as any)?.salesperson?.name || "",
        };
        dataLayer.push(googleAnalyticsData);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.titleWrapper}>
                <Typography {...styles.title}>{fields.title}</Typography>
            </StyledWrapper>

            <StyledWrapper {...styles.phoneHoursWrapper}>
                <StyledWrapper>
                    <StyledWrapper {...styles.phoneWrapper}>
                        <Link
                            {...styles.phone}
                            href={`tel:${phoneNumber}`}
                            target="_blank"
                            aria-label={phoneNumber}
                            onClick={_GaFooterTelEvent}
                        >
                            {phoneNumber}
                        </Link>
                    </StyledWrapper>

                    {isAuthenticated && warehouseHours && (
                        <Typography as="p" {...styles.hours}>
                            {warehouseHours}
                        </Typography>
                    )}
                </StyledWrapper>

                {isAuthenticated && (
                    <Hidden as="div" above="md">
                        <Link
                            href={`sms:${phoneNumber}`}
                            {...styles.prokeep}
                            target="_blank"
                            icon={{ iconProps: { src: Prokeep } }}
                            aria-label={`SMS ${phoneNumber}`}
                        >
                            {" "}
                            <VisuallyHidden>{siteMessage("Footer_Prokeep")}</VisuallyHidden>
                        </Link>
                    </Hidden>
                )}
            </StyledWrapper>

            <Link {...styles.email} href={`mailto:${email}`} aria-label={email} onClick={_GaFooterEmailEvent}>
                {email}
            </Link>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(SupportInformation),
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Support Services",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.phoneNumber,
                displayName: "Corporate Phone Number",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
                regularExpression: xRegex.phone,
            },
            {
                name: fields.email,
                displayName: "Corporate Email Address",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "testaccount@insitesoft.com",
                isRequired: true,
                regularExpression: xRegex.email,
            },
        ],
    },
};

export default widgetModule;
